<script setup lang="ts" generic="V extends string | number">
/*
 * Sub-nav style tabs matching the prototype's .sub-nav rule (bottom-border
 * underline, active = primary text + primary border, optional count badge).
 *
 * Each tab may carry an optional `count` rounded-pill badge:
 *   - a number       → the number is shown
 *   - null/undefined → an em-dash ("—") is shown (data not available yet)
 *   - omitted (no `count` key) → no badge at all
 * The active tab's badge is brand-tinted; inactive badges are grey. A thin
 * full-width gray baseline runs under the whole strip; the active tab adds a
 * brand-colour underline on top of it.
 */
import { computed, type Component } from 'vue';

interface Tab<T> {
  key: T;
  label: string;
  /**
   * Optional count badge. A number renders the number; an explicit
   * null/undefined renders an em-dash ("—"). Omit the key entirely for no badge.
   */
  count?: number | null;
  /** Optional lucide (or any Vue component) icon rendered before the label. */
  icon?: Component;
}

const props = defineProps<{
  modelValue: V;
  tabs: Tab<V>[];
}>();

defineEmits<{ 'update:modelValue': [v: V] }>();

const activeKey = computed(() => props.modelValue);

/** Whether a tab declares a count slot at all (number OR explicit null/undefined). */
function hasBadge(t: Tab<V>): boolean {
  return 'count' in t;
}

/** Badge text: the number, or an em-dash when the count is null/undefined. */
function badgeText(t: Tab<V>): string {
  return typeof t.count === 'number' ? String(t.count) : '—';
}

/**
 * Whether the badge currently shows the em-dash placeholder (count unavailable)
 * rather than a real number. Such a badge carries no information a screen
 * reader needs — the tab label already names the item — and screen readers
 * announce "—" as "em dash"/"minus", so we hide it from the a11y tree.
 */
function isEmDashBadge(t: Tab<V>): boolean {
  return typeof t.count !== 'number';
}
</script>

<template>
  <div class="flex gap-1 overflow-x-auto border-b border-app-border">
    <button
      v-for="t in tabs"
      :key="String(t.key)"
      type="button"
      :data-testid="`tab-${t.key}`"
      :class="[
        '-mb-px flex items-center gap-1.5 whitespace-nowrap border-b-2 px-3 py-2.5 text-base font-medium transition-colors',
        t.key === activeKey
          ? 'border-primary text-primary'
          : 'border-transparent text-slate-500 hover:text-app-fg',
      ]"
      :aria-selected="t.key === activeKey"
      role="tab"
      @click="$emit('update:modelValue', t.key)"
    >
      <component :is="t.icon" v-if="t.icon" class="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      {{ t.label }}
      <span
        v-if="hasBadge(t)"
        :data-testid="`tab-count-${t.key}`"
        :aria-hidden="isEmDashBadge(t) ? 'true' : undefined"
        :class="[
          'inline-flex min-w-[1.5rem] items-center justify-center rounded-pill px-2 py-0.5 text-[11px] font-medium tabular-nums',
          t.key === activeKey
            ? 'bg-primary-100 text-primary-700'
            : 'bg-app-border-soft text-app-muted',
        ]"
      >
        {{ badgeText(t) }}
      </span>
    </button>
  </div>
</template>
